import { ExtendCSS, getTheme } from 'vcc-ui';

import { colorChangeTransition } from '@collab/style/customThemeTokens';

const volvo = getTheme({ variant: 'light' });

const fontSizeH4 = '1.25rem';
const fontSizeParagraph = '1rem';
const fontSizeSmall = '0.75rem';

const baseButtonStyle: ExtendCSS = {
  boxShadow: 'none',
  borderRadius: `${volvo.baselineSubGrid}px !important`,
  borderWidth: 1,
  borderStyle: 'solid',
  padding: `9px ${volvo.baselineGrid * 2}px`,
  textTransform: 'uppercase',
  lineHeight: '20px',
  fontSize: fontSizeSmall,
  transitionProperty: 'color, background-color, background, border',
  transitionDuration: '300ms',
  transitionTimingFunction: 'ease-out',
  fontWeight: volvo.font.weight.medium,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textShadow: 'none !important',
};

const formOutlineStyle = `solid 1px ${volvo.color.foreground.action}`;

const buttonColorStyles = {
  primary: {
    background: volvo.color.brand.primary,
    borderColor: volvo.color.brand.primary,
    color: volvo.color.foreground.inverted,
    '&:focus-visible': volvo.states.focus,
  },
  primaryHover: {
    background: volvo.color.primitive.black,
    borderColor: volvo.color.brand.primary,
  },
  outline: {
    borderColor: volvo.color.foreground.action,
    color: volvo.color.foreground.action,
    '&:focus-visible': volvo.states.focus,
  },
  outlineHover: {
    background: volvo.color.brand.primary,
    borderColor: volvo.color.brand.primary,
    color: volvo.color.background.primary,
  },
  alert: {
    borderColor: volvo.color.foreground.alert,
    color: volvo.color.foreground.alert,
    '&:focus-visible': volvo.states.focus,
  },
  alertHover: {
    background: volvo.color.foreground.alert,
    color: volvo.color.background.primary,
    borderColor: volvo.color.foreground.alert,
  },
};

const alertFontColor: ExtendCSS = {
  color: volvo.color.foreground.alert,
};

const endpointStyle: ExtendCSS = {
  background: 'none !important',
  borderColor: volvo.color.ornament.divider,
  borderTop: 0,
  boxShadow: 'none',
  borderRadius: 0,
  marginBottom: 0,
};

const baseFormStyle: ExtendCSS = {
  border: `1px solid ${volvo.color.ornament.divider}`,
  boxShadow: 'none',
  background: volvo.color.background.secondary,
  fontWeight: volvo.font.weight.medium,
  outline: 'none',
};

const heading4Style: ExtendCSS = {
  fontSize: `${fontSizeH4} !important`,
  fontWeight: volvo.font.weight.medium,
  whiteSpace: 'nowrap',
  color: volvo.color.foreground.primary,
};

const paragraphColorStyle: ExtendCSS = {
  color: volvo.color.foreground.secondary,
};

const fontSizeBates: ExtendCSS = {
  fontSize: fontSizeSmall,
};

const swaggerStyle: ExtendCSS = {
  '& .swagger-ui *': {
    fontFamily: 'Volvo Novum, Arial, sans-serif !important',
  },

  '& .swagger-ui .wrapper': {
    padding: 0,
  },

  // Swagger information box
  '& .swagger-ui .info': {
    fontWeight: volvo.font.weight.medium,
    fontSize: fontSizeParagraph,
    margin: 0,
  },

  '& .swagger-ui p': {
    fontSize: `${fontSizeParagraph} !important`,
    fontWeight: volvo.font.weight['semi-light'],
    ...paragraphColorStyle,
  },

  '& .swagger-ui .parameter__name': paragraphColorStyle,
  '& .swagger-ui .responses-inner > div': paragraphColorStyle,
  '& .swagger-ui parameter__type': paragraphColorStyle,
  '& .swagger-ui .response-col_status': paragraphColorStyle,
  '& .swagger-ui opblock-summary-description': paragraphColorStyle,
  '& .swagger-ui .response-col_links': paragraphColorStyle,

  '& .swagger-ui .tab li': {
    ...paragraphColorStyle,
    ...fontSizeBates,
    marginBottom: volvo.baselineSubGrid,
    transition: 'color 100ms linear',
    '& > button:focus-visible': {
      outlineColor: volvo.color.foreground.primary,
    },
  },

  '& .swagger-ui .tab li.active': {
    fontWeight: volvo.font.weight.medium,
    color: volvo.color.foreground.primary,
  },

  '& .swagger-ui .tab li:hover': {
    color: volvo.color.foreground.primary,
  },

  '& .swagger-ui .parameter__type': {
    ...paragraphColorStyle,
    ...fontSizeBates,
  },

  '& .swagger-ui .opblock-body select': {
    ...paragraphColorStyle,
    width: '100%',
  },

  '& .swagger-ui table tbody tr td': {
    ...paragraphColorStyle,
    paddingBottom: volvo.baselineGrid,
  },

  '& .swagger-ui .renderedMarkdown p': { marginTop: 0, marginBlockEnd: 0 },

  '& .swagger-ui .parameters-col_description .renderedMarkdown p': {
    marginTop: 0,
  },

  '& .swagger-ui .opblock .opblock-summary-description': {
    ...paragraphColorStyle,
    ...fontSizeBates,
    paddingTop: volvo.baselineSubGrid,
    paddingLeft: 10,
    gridArea: '2 / 2',
  },

  '& .swagger-ui .opblock-description-wrapper .renderedMarkdown p': {
    marginTop: '1em',
    marginBottom: volvo.baselineGrid,
  },

  '& .swagger-ui .response-control-media-type__accept-message': {
    ...fontSizeBates,
    color: volvo.color.foreground.positive,
  },

  '& .swagger-ui .col_header': {
    fontWeight: volvo.font.weight.medium,
    paddingBottom: `${volvo.baselineGrid}px !important`,
    fontSize: fontSizeParagraph,
    borderBottom: `1px solid ${volvo.color.ornament.divider}`,
    color: volvo.color.foreground.primary,
  },

  '& .swagger-ui h4': heading4Style,
  '& .swagger-ui .opblock .opblock-section-header h4': heading4Style,
  '& .swagger-ui section.models h4': heading4Style,

  '& .swagger-ui .responses-inner h4': {
    padding: 0,
    margin: `${volvo.baselineGrid * 5}px 0 ${
      volvo.baselineGrid
    }px 0 !important`,
  },

  '& .swagger-ui h5': {
    ...paragraphColorStyle,
    marginTop: volvo.baselineGrid * 2,
    fontWeight: volvo.font.weight.medium,
  },

  '& .swagger-ui .response-control-media-type__title': {
    ...fontSizeBates,
    marginBottom: volvo.baselineGrid,
    fontWeight: volvo.font.weight.medium,
  },

  '& .swagger-ui .model-box': {
    width: '100%',
    background: volvo.color.background.secondary,
    border: `1px solid ${volvo.color.ornament.divider}`,
  },

  // Form styles
  '& .swagger-ui .parameter__name.required:after': alertFontColor,
  '& .swagger-ui .parameter__name.required span': alertFontColor,

  '& .swagger-ui textarea': baseFormStyle,
  '& .swagger-ui textarea:focus-visible': {
    outline: formOutlineStyle,
    border: 'none',
  },

  '& .swagger-ui select': baseFormStyle,

  '& .swagger-ui select:focus-visible': {
    outline: formOutlineStyle,
  },

  '& .swagger-ui input[type=text]:focus-visible': {
    border: formOutlineStyle,
    outline: 'none',
  },

  '& .swagger-ui input[type=text].invalid': {
    borderColor: volvo.color.foreground.alert,
    background: 'none',
    '&:focus-visible': {
      border: formOutlineStyle,
      outline: 'none',
    },
  },

  // Endpoint styles
  '& .swagger-ui .opblock .opblock-section-header': {
    boxShadow: 'none',
    borderBottom: `1px solid ${volvo.color.ornament.divider}`,
    gap: volvo.baselineGrid * 2,
  },

  '& .swagger-ui .opblock .tab-header .tab-item.active h4 span:after': {
    bottom: -volvo.baselineGrid * 2,
  },

  '& .swagger-ui .opblock .tab-header .tab-item': {
    cursor: 'inherit',
  },

  '& .swagger-ui .opblock.opblock-get .tab-header .tab-item.active h4 span:after':
    {
      background: volvo.color.foreground.action,
    },

  '& .swagger-ui .opblock.opblock-post .tab-header span:after': {
    background: volvo.color.foreground.action,
  },

  '& .swagger-ui .opblock .opblock-summary-path': {
    fontWeight: volvo.font.weight.medium,
    maxWidth: '100%',
    width: 'auto',
  },

  '& .swagger-ui .opblock-tag-section.is-open .opblock-tag': {
    background: volvo.color.background.secondary,
  },

  '& .swagger-ui button.opblock-summary-control': {
    display: 'grid',
    gridTemplate: 'auto auto / 80px auto',
  },

  '& .swagger-ui button.opblock-summary-control:focus-visible': {
    ...volvo.states.focus,
    outlineOffset: 4,
  },

  '& .swagger-ui .opblock-summary-control:focus': { outline: 'none' },

  '& .swagger-ui .opblock-body': endpointStyle,
  '& .swagger-ui .opblock.opblock-get': endpointStyle,
  '& .swagger-ui .opblock.opblock-post': endpointStyle,
  '& .swagger-ui .opblock.opblock-delete': endpointStyle,
  '& .swagger-ui .opblock.opblock-put': endpointStyle,

  '& .swagger-ui .opblock.opblock-get .opblock-summary': {
    ...endpointStyle,
    borderColor: volvo.color.foreground.action,
  },

  '& .swagger-ui h3.opblock-tag': {
    fontWeight: volvo.font.weight.medium,
    fontSize: fontSizeParagraph,
    marginBottom: 0,
    padding: `${volvo.baselineGrid * 2}px ${volvo.baselineGrid * 4}px`,
    borderBottomColor: volvo.color.ornament.divider,
  },

  '& .swagger-ui .opblock.opblock-post .opblock-summary-method': {
    background: volvo.color.foreground.positive,
  },

  '& .swagger-ui .opblock.opblock-delete .opblock-summary-method': {
    background: volvo.color.foreground.alert,
  },

  '& .swagger-ui .opblock.opblock-put .opblock-summary-method': {
    background: 'var(--v-color-foreground-feedback-orange)',
  },

  '& .swagger-ui .opblock button.opblock-summary-control .opblock-summary-method':
    {
      fontWeight: volvo.font.weight.medium,
      lineHeight: '20px',
      fontSize: fontSizeSmall,
      padding: `${volvo.baselineGrid}px 0`,
      gridRow: 'span 2',
    },

  '& .swagger-ui .opblock.opblock-get button.opblock-summary-control .opblock-summary-method':
    {
      background: volvo.color.foreground.action,
      borderColor: volvo.color.brand.primary,
    },

  '& .swagger-ui .opblock-summary-path-description-wrapper': {
    alignItems: 'flex-start',
    flexDirection: 'column',
  },

  '& .swagger-ui .opblock-summary-path-description-wrapper .opblock-summary-description':
    {
      paddingLeft: 0,
    },

  // Copy to clipboard button
  '& .swagger-ui .opblock .opblock-summary .view-line-link': {
    marginRight: volvo.baselineGrid,
    width: 24,
    height: 24,
    background: 'none',
    opacity: 0.6,
    backgroundPosition: 'center',
    backgroundSize: '20px 20px',
    backgroundRepeat: 'no-repeat',
    backgroundImage:
      'url(https://www.volvocars.com/static/shared/icons/v2/copy-16.svg#light-primary)',
  },

  '& .swagger-ui .opblock .opblock-summary .opblock-control-arrow': {
    marginRight: volvo.baselineGrid,
    width: 24,
    height: 24,
  },

  '& .swagger-ui .opblock .opblock-summary .authorization__btn': {
    marginRight: volvo.baselineGrid,
    padding: 0,
    width: 24,
    height: 24,
  },

  //Hide svg copy icon that swagger has
  '& .swagger-ui .opblock .opblock-summary .view-line-link > svg': {
    opacity: 0,
  },

  '& .swagger-ui .opblock .opblock-summary .view-line-link:hover': {
    opacity: 1,
  },

  '& .swagger-ui .opblock .opblock-summary .view-line-link:active': {
    transform: 'scale(0.8)',
  },

  // authorization button (the lock svg)
  '& .swagger-ui .authorization__btn .unlocked': {
    padding: 0,
    opacity: 0.5,
    fill: volvo.color.foreground.primary,
    transition: colorChangeTransition,
    '&:focus-visible': {
      ...volvo.states.focus,
      opacity: 1,
    },
  },

  '& .swagger-ui .authorization__btn .unlocked:hover': {
    opacity: 1,
  },

  // Swagger authorization block
  '& .swagger-ui .scheme-container': {
    boxShadow: 'none',
    padding: 0,
    paddingBottom: volvo.baselineGrid * 2,
  },

  '& .swagger-ui .servers-title': {
    fontSize: fontSizeH4,
    marginBottom: volvo.baselineGrid,
    fontWeight: volvo.font.weight.medium,
    display: 'block',
  },

  // Buttons
  '& .swagger-ui .btn': baseButtonStyle,

  '& .swagger-ui .btn:hover': {
    boxShadow: 'none',
    background: volvo.color.brand.primary,
    color: volvo.color.background.primary,
  },

  '& .swagger-ui .btn.authorize': {
    display: 'flex',
    justifyContent: 'center',
    ...buttonColorStyles.outline,
  },
  '& .swagger-ui .btn.authorize:hover': buttonColorStyles.outlineHover,

  '& .swagger-ui .btn.authorize span': {
    padding: `0 ${volvo.baselineSubGrid * 3}px 0 0`,
  },

  '& .swagger-ui .expand-operation:focus-visible': volvo.states.focus,

  '& .swagger-ui .btn.authorize > svg': {
    fill: volvo.color.foreground.action,
    transition: '0.3s ease',
  },

  '& .swagger-ui .btn.authorize:hover > svg': {
    fill: volvo.color.background.primary,
  },

  '& .swagger-ui button.tablinks': {
    outlineOffset: 2,
  },

  // auth popup for authorization
  '& .swagger-ui .auth-btn-wrapper': {
    display: 'flex',
    columnGap: volvo.baselineGrid * 3,
  },
  '& .swagger-ui .auth-btn-wrapper .btn': {
    ...buttonColorStyles.outline,
    margin: 0,
  },
  '& .swagger-ui .auth-btn-wrapper .btn:hover': buttonColorStyles.outlineHover,

  '& .swagger-ui .btn-group': { gap: volvo.baselineGrid * 2 },

  '& .swagger-ui .btn.execute': buttonColorStyles.primary,
  '& .swagger-ui .btn.execute:hover': buttonColorStyles.primaryHover,

  '& .swagger-ui .btn.cancel': buttonColorStyles.alert,
  '& .swagger-ui .btn.cancel:hover': buttonColorStyles.alertHover,

  '& .swagger-ui .try-out__btn': buttonColorStyles.outline,
  '& .swagger-ui .try-out__btn:hover': buttonColorStyles.outlineHover,

  '& .swagger-ui .btn-clear': buttonColorStyles.outline,
  '& .swagger-ui .btn-clear:hover': buttonColorStyles.outlineHover,

  // "schemas" button/area
  '& .swagger-ui .models-control:focus': {
    outline: 'none',
  },

  '& .swagger-ui button.models-control:focus-visible': {
    ...volvo.states.focus,
    outlineOffset: 9,
  },

  // Schema block
  '& .swagger-ui section.models': {
    borderRadius: 0,
    border: `1px solid ${volvo.color.ornament.divider}`,
    marginBottom: 0,
  },

  '& .swagger-ui .model-title': {
    color: volvo.color.foreground.primary,
    fontSize: fontSizeParagraph,
    fontWeight: volvo.font.weight.medium,
  },

  '& .swagger-ui section.models.is-open h4': {
    borderBottom: `1px solid ${volvo.color.ornament.divider}`,
  },

  '& .swagger-ui section.models .model-container': {
    background: volvo.color.background.secondary,
    border: `1px solid ${volvo.color.ornament.divider}`,
    borderRadius: 0,
  },

  '& .swagger-ui section.models .model-box': {
    border: 0,
    borderRadius: 0,
  },

  '& .swagger-ui .model-box-control': {
    display: 'flex',
    alignItems: 'center',
  },

  '& .swagger-ui section.models .model-box-control .pointer .model-box': {
    paddingRight: 0,
  },

  '& .swagger-ui .model-toggle': {
    top: 1,
  },

  '& .swagger-ui section.models h4:hover': {
    background: 'rgba(0,0,0,.02)',
  },

  '& .swagger-ui section.models h4:hover button': {
    background: 'none',
  },
};

export default swaggerStyle;
